import { ActionModal, FormLayout } from '@kcftech/design-system';
import { updatePassword } from 'auth-provider';
import { PasswordInput } from 'components/forms/PasswordInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { IUserContext } from 'contexts/UserContext';
import { logout } from 'pages/Login/Login';
import { PasswordRequirements } from 'pages/Login/PasswordRequirements';
import { getPasswordRules } from 'pages/Login/shared';
import { useForm } from 'react-hook-form';

export type ChangePasswordPopupModalProps = {
    userContext: IUserContext;
    onClose: () => void;
};

type PasswordChangeFormData = {
    previousPassword: string;
    newPassword: string;
    confirmNewPassword: string;
};

export function ChangePasswordPopupModal({
    userContext,
    onClose,
}: ChangePasswordPopupModalProps) {
    const { showToast } = useNotificationContext();

    const {
        register,
        getValues,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
        setError,
    } = useForm<PasswordChangeFormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            previousPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        },
    });

    const validatePreviousPassword = (previousPassword: string) => {
        if (previousPassword === '') {
            return 'Current password is required.';
        }
        return undefined;
    };

    const validateConfirmNewPassword = (confirmNewPassword: string) => {
        const isPasswordMatch = getValues().newPassword === confirmNewPassword;

        if (!isPasswordMatch) {
            return 'Passwords must match. Please enter matching passwords.';
        }
        return undefined;
    };

    const changePassword = (data: PasswordChangeFormData) => {
        if (
            userContext.user?.IdentityProviderName !== undefined ||
            !userContext.user?.IsApproved
        ) {
            return false;
        }

        updatePassword({
            oldPassword: data.previousPassword,
            newPassword: data.newPassword,
        })
            .then(() => {
                showToast({
                    type: 'success',
                    content: 'Password changed successfully.',
                });
                onClose();
                setTimeout(() => logout(true), 200);
            })
            .catch((err) => {
                if (typeof err === 'object' && err && 'message' in err) {
                    setError('newPassword', { message: err.message });
                } else {
                    showToast({
                        type: 'error',
                        content:
                            'Failed to process password change request. Please contact KCF support.',
                    });
                }
            });
        return true;
    };

    return (
        <ActionModal
            title="Change Password"
            onAction={handleSubmit(changePassword)}
            actionable={
                (!isSubmitted || isValid) &&
                !(isValidating || isSubmitting) &&
                getValues().newPassword.trim() !== ''
            }
            actionButtonText="Submit"
            closeButtonText="Cancel"
            onClose={onClose}
            height={630}
        >
            <PasswordRequirements />
            <FormLayout>
                <ValidatableLabel
                    label="Current Password"
                    error={errors.previousPassword}
                    required
                >
                    <PasswordInput
                        autofocus
                        placeholder="Enter your current password"
                        className="my-5"
                        required
                        invalid={errors.previousPassword !== undefined}
                        {...register('previousPassword', {
                            validate: validatePreviousPassword,
                        })}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="New Password"
                    error={errors.newPassword}
                    required
                >
                    <PasswordInput
                        placeholder="Enter a new password"
                        className="my-5"
                        required
                        invalid={errors.newPassword !== undefined}
                        showValidState={getValues().newPassword !== ''}
                        {...register(
                            'newPassword',
                            getPasswordRules(getValues().previousPassword),
                        )}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="Confirm New Password"
                    error={errors.confirmNewPassword}
                    required
                >
                    <PasswordInput
                        placeholder="Confirm your new password"
                        className="my-5"
                        required
                        invalid={errors.confirmNewPassword !== undefined}
                        showValidState={getValues().confirmNewPassword !== ''}
                        {...register('confirmNewPassword', {
                            validate: validateConfirmNewPassword,
                        })}
                    />
                </ValidatableLabel>
            </FormLayout>
        </ActionModal>
    );
}
